/**
 * Admin view of access requests — Gap 3 Path C.
 *
 * Lists submitted requests with pending/approved/rejected filters.
 * Approve opens an inline form (XI company_id required) that mints a
 * single-use 30-day invitation code in one shot. The code shows up
 * inline so the operator can copy + email it. Reject takes an
 * optional note + spam flag.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError} from "../../api/client";
import { apiGet, apiPost } from "../../api/client";
import { AdminShell } from "../../components/admin/AdminShell";

type Status = "pending" | "approved" | "rejected" | "spam";

interface AccessRequest {
  id: string;
  email: string;
  displayName: string | null;
  company: string | null;
  useCase: string | null;
  status: Status;
  payload: Record<string, unknown>;
  createdAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
  invitationCode: string | null;
}

const STATUS_TONE: Record<Status, "approve" | "return" | "note"> = {
  pending: "note",
  approved: "approve",
  rejected: "return",
  spam: "return",
};

export function AdminAccessRequests() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<Status | "">("pending");
  const [openId, setOpenId] = useState<string | null>(null);

  const q = useQuery<{ requests: AccessRequest[] }, ApiError>({
    queryKey: ["admin-access-requests", statusFilter],
    queryFn: () =>
      apiGet(`/api/admin/access-requests${statusFilter ? `?status=${statusFilter}` : ""}`),
    retry: false,
  });

  const counts = useMemo(() => {
    const arr = q.data?.requests ?? [];
    return {
      total: arr.length,
      pending: arr.filter((r) => r.status === "pending").length,
    };
  }, [q.data]);

  return (
    <AdminShell>
    <main className="review-page" style={{ margin: "0 auto" }}>
      <p className="review-eyebrow">xamun owner</p>
      <h1 className="review-title">Access requests</h1>
      <p className="review-meta">
        People asking to try PlayStudio. Approve to mint an invitation code
        in one shot; reject with a note for the audit trail.
      </p>

      {q.error?.status === 403 && (
        <div className="review-banner error" style={{ marginTop: 16 }}>
          You're not on the Xamun admin allow-list.
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center" }}>
        <select
          className="review-input"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as Status | "")}
          style={{ flex: "0 0 200px" }}
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="spam">Spam</option>
        </select>
        <span style={{ fontSize: 12, color: "var(--text-3)" }}>
          {counts.total} entries{statusFilter ? "" : ` · ${counts.pending} pending`}
        </span>
      </div>

      <section className="review-section">
        {q.isLoading ? (
          <div style={{ color: "var(--text-3)" }}>Loading…</div>
        ) : !q.data || q.data.requests.length === 0 ? (
          <div className="ho-empty">No requests match.</div>
        ) : (
          <ul className="review-comment-list">
            {q.data.requests.map((r) => (
              <li key={r.id} className="review-comment">
                <div className="review-comment-head">
                  <strong className="author">{r.displayName ?? r.email}</strong>
                  <span className={`review-comment-decision ${STATUS_TONE[r.status]}`}>
                    {r.status}
                  </span>
                  {r.company && (
                    <span style={{ color: "var(--text-3)" }}>· {r.company}</span>
                  )}
                  <span style={{ marginLeft: "auto", fontSize: 11 }}>
                    {new Date(r.createdAt).toLocaleString()}
                  </span>
                </div>
                <div style={{ marginTop: 6, fontSize: 13, color: "var(--text-2)" }}>
                  {r.email}
                </div>
                {r.useCase && (
                  <p className="review-comment-body" style={{ marginTop: 6 }}>
                    {r.useCase}
                  </p>
                )}

                {r.invitationCode && (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    Code minted:{" "}
                    <code style={{ fontFamily: "monospace" }}>
                      {r.invitationCode}
                    </code>
                  </div>
                )}
                {r.decisionNote && (
                  <div
                    className="review-anchor-chip"
                    style={{ marginTop: 6 }}
                  >
                    {r.decisionNote}
                  </div>
                )}

                {r.status === "pending" && (
                  <div style={{ marginTop: 8 }}>
                    {openId === r.id ? (
                      <DecideForm
                        requestId={r.id}
                        onDone={async () => {
                          setOpenId(null);
                          await qc.invalidateQueries({ queryKey: ["admin-access-requests"] });
                        }}
                        onCancel={() => setOpenId(null)}
                      />
                    ) : (
                      <button
                        type="button"
                        className="review-btn approve"
                        onClick={() => setOpenId(r.id)}
                      >
                        Decide
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
    </AdminShell>
  );
}

function DecideForm({
  requestId,
  onDone,
  onCancel,
}: {
  requestId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [companyId, setCompanyId] = useState("");
  const [playstudioOnly, setPlaystudioOnly] = useState(false);
  const [workspaceNameDefault, setWorkspaceNameDefault] = useState("");
  const [ttlDays, setTtlDays] = useState("30");
  const [note, setNote] = useState("");

  const approve = useMutation<unknown, ApiError, void>({
    mutationFn: () =>
      apiPost(`/api/admin/access-requests/${requestId}/approve`, {
        ...(companyId.trim() && { company_id: companyId.trim() }),
        ...(playstudioOnly && { playstudio_only: true }),
        ...(workspaceNameDefault.trim() && {
          workspace_name_default: workspaceNameDefault.trim(),
        }),
        ...(ttlDays && Number(ttlDays) > 0 && { ttl_days: Number(ttlDays) }),
        ...(note.trim() && { decision_note: note.trim() }),
      }),
    onSuccess: onDone,
  });

  const reject = useMutation<unknown, ApiError, { spam?: boolean }>({
    mutationFn: (args) =>
      apiPost(`/api/admin/access-requests/${requestId}/reject`, {
        ...(note.trim() && { decision_note: note.trim() }),
        ...(args.spam && { spam: true }),
      }),
    onSuccess: onDone,
  });

  const canApprove =
    (companyId.trim().length > 0 || playstudioOnly) && !approve.isPending;

  return (
    <div className="review-form" style={{ marginTop: 8 }}>
      <h3 className="review-section-title" style={{ marginBottom: 10 }}>
        Decide
      </h3>
      <label className="review-field">
        <span className="review-field-label">
          <strong>XI company id</strong>
          <span className="review-field-hint">
            required to approve — defines which XI tenant the new workspace belongs to
          </span>
        </span>
        <input
          className="review-input"
          value={companyId}
          onChange={(e) => {
            setCompanyId(e.target.value);
            if (e.target.value.trim()) setPlaystudioOnly(false);
          }}
          placeholder="co_acme_corp"
          disabled={playstudioOnly}
        />
      </label>
      <label
        className="review-field"
        style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}
      >
        <input
          type="checkbox"
          checked={playstudioOnly}
          onChange={(e) => {
            setPlaystudioOnly(e.target.checked);
            if (e.target.checked) setCompanyId("");
          }}
          style={{ width: "auto" }}
        />
        <span style={{ fontSize: 13 }}>
          <strong>PlayStudio-only tenant</strong>
        </span>
      </label>

      <div className="spotted-form-row" style={{ marginTop: 12 }}>
        <label className="spotted-field">
          <span>Default workspace name (optional)</span>
          <input
            className="review-input"
            value={workspaceNameDefault}
            onChange={(e) => setWorkspaceNameDefault(e.target.value)}
          />
        </label>
        <label className="spotted-field">
          <span>Code TTL (days)</span>
          <input
            className="review-input"
            type="number"
            min={1}
            value={ttlDays}
            onChange={(e) => setTtlDays(e.target.value)}
          />
        </label>
      </div>

      <label className="review-field">
        <span className="review-field-label">
          <strong>Note (optional)</strong>
        </span>
        <input
          className="review-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Visible in the audit log"
        />
      </label>

      {(approve.error || reject.error) && (
        <div className="review-banner error" style={{ marginTop: 12 }}>
          {(approve.error ?? reject.error)?.status === 400
            ? "Provide either an XI company_id or check PlayStudio-only."
            : `Could not decide (${(approve.error ?? reject.error)?.status}).`}
        </div>
      )}

      <div className="review-actions" style={{ marginTop: 14 }}>
        <button
          type="button"
          className="review-btn approve"
          disabled={!canApprove}
          onClick={() => approve.mutate()}
        >
          {approve.isPending ? "Approving…" : "Approve + mint code"}
        </button>
        <button
          type="button"
          className="review-btn return"
          disabled={reject.isPending}
          onClick={() => reject.mutate({})}
        >
          {reject.isPending ? "Rejecting…" : "Reject"}
        </button>
        <button
          type="button"
          className="review-btn note"
          disabled={reject.isPending}
          onClick={() => reject.mutate({ spam: true })}
        >
          Mark as spam
        </button>
        <button type="button" className="review-btn note" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
